import { useEffect, useState } from 'react';
import type { CategoriaMotivoPerdido, OrcamentoResponse } from '@triade/contracts';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Label } from '@/components/ui/label';
import { ApiError } from '@/lib/api';
import { ROTULO_CATEGORIA } from './tipos';

type Status = OrcamentoResponse['status'];

export interface DadosSituacao {
  motivo?: string;
  categoria?: CategoriaMotivoPerdido;
}

const TEXTO: Record<Exclude<Status, 'RASCUNHO'>, { titulo: (n: number) => string; descricao: string; botao: string }> = {
  ENVIADO: {
    titulo: (n) => `Enviar o orçamento ${n}?`,
    descricao: 'Depois de enviado o orçamento fica congelado: itens e preços não mudam mais (somente leitura); muda só a situação.',
    botao: 'Enviar',
  },
  EM_NEGOCIACAO: {
    titulo: (n) => `Marcar o orçamento ${n} como em negociação?`,
    descricao: 'O orçamento continua congelado. Depois só pode ser aprovado ou marcado como perdido.',
    botao: 'Em negociação',
  },
  APROVADO: {
    titulo: (n) => `Aprovar o orçamento ${n}?`,
    descricao: 'Aprovado é uma situação final: o orçamento não muda mais.',
    botao: 'Aprovar',
  },
  PERDIDO: {
    titulo: (n) => `Marcar o orçamento ${n} como perdido?`,
    descricao: 'Perdido é uma situação final. Informe o motivo: ele fica registrado no histórico.',
    botao: 'Marcar como perdido',
  },
};

const CAMPO = 'w-full rounded-[10px] border border-input bg-card px-3 text-body outline-none focus:border-primary focus:shadow-focus';
const ROTULO = 'mb-1.5 block text-caption font-medium text-ink-secondary';

export function mensagemErroSituacao(e: unknown): string {
  return e instanceof ApiError ? `${e.message}${e.code ? ` (${e.code})` : ''}` : 'falha de comunicação com a API';
}

export interface DialogoSituacaoProps {
  numero: number;
  /** Situacao de destino; nulo = fechado. */
  para: Status | null;
  /** Chama a API; se rejeitar, o dialogo continua aberto e mostra o erro. Resolver fecha. */
  onConfirmar: (dados: DadosSituacao) => Promise<void>;
  onCancelar: () => void;
}

/**
 * Confirma a mudanca de situacao. Para PERDIDO pede categoria (opcional) e motivo (obrigatorio) ANTES de chamar a API.
 * Usado pelo kanban (arrastar/menu) e pelo detalhe do orcamento.
 */
export function DialogoSituacao({ numero, para, onConfirmar, onCancelar }: DialogoSituacaoProps) {
  const [motivo, setMotivo] = useState('');
  const [categoria, setCategoria] = useState<CategoriaMotivoPerdido | ''>('');
  const [erro, setErro] = useState<string | null>(null);
  const [ocupado, setOcupado] = useState(false);
  useEffect(() => {
    setMotivo('');
    setCategoria('');
    setErro(null);
    setOcupado(false);
  }, [para]);

  const perdido = para === 'PERDIDO';
  const t = para && para !== 'RASCUNHO' ? TEXTO[para] : null;
  const motivoVazio = perdido && motivo.trim() === '';

  async function confirmar() {
    if (motivoVazio) {
      setErro('Informe o motivo da perda.');
      return;
    }
    setErro(null);
    setOcupado(true);
    try {
      await onConfirmar(perdido ? { motivo: motivo.trim(), ...(categoria ? { categoria } : {}) } : {});
    } catch (e) {
      setErro(mensagemErroSituacao(e));
      setOcupado(false);
    }
  }

  return (
    <AlertDialog open={para !== null} onOpenChange={(o) => !o && !ocupado && onCancelar()}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>{t ? t.titulo(numero) : ''}</AlertDialogTitle>
          <AlertDialogDescription>{t?.descricao ?? ''}</AlertDialogDescription>
        </AlertDialogHeader>
        {perdido && (
          <div className="flex flex-col gap-3">
            <div>
              <Label htmlFor="perdido-categoria" className={ROTULO}>
                Categoria (opcional)
              </Label>
              <select
                id="perdido-categoria"
                value={categoria}
                onChange={(e) => setCategoria(e.target.value as CategoriaMotivoPerdido | '')}
                className={`${CAMPO} h-10`}
              >
                <option value="">Sem categoria</option>
                {(Object.keys(ROTULO_CATEGORIA) as CategoriaMotivoPerdido[]).map((c) => (
                  <option key={c} value={c}>
                    {ROTULO_CATEGORIA[c]}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <Label htmlFor="perdido-motivo" className={ROTULO}>
                Motivo
              </Label>
              <textarea
                id="perdido-motivo"
                value={motivo}
                onChange={(e) => setMotivo(e.target.value)}
                maxLength={1000}
                rows={3}
                required
                aria-required="true"
                aria-invalid={erro !== null && motivoVazio}
                className={`${CAMPO} py-2`}
              />
            </div>
          </div>
        )}
        {erro && (
          <p role="alert" data-testid="erro-situacao" className="rounded-xl border border-destructive/30 bg-danger-bg px-3 py-2 text-small text-danger-ink">
            {erro}
          </p>
        )}
        <AlertDialogFooter>
          <AlertDialogCancel disabled={ocupado}>Voltar</AlertDialogCancel>
          <AlertDialogAction
            disabled={ocupado}
            onClick={(e) => {
              e.preventDefault(); // o pai fecha ao resolver; erro mantem aberto
              void confirmar();
            }}
          >
            {t?.botao ?? ''}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
